/**
 * ConfirmHost — app-wide replacement for the browser's native confirm()/alert().
 *
 * Mount <ConfirmHost /> once near the app root. Then anywhere in the app call:
 *
 *     if (!(await confirmDialog('Delete this item?'))) return;
 *     await alertDialog('Upload failed');
 *
 * Both return Promises, so they're (almost) drop-in replacements for the native
 * dialogs — just `await` them. Styled as a centered modal popup.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Info } from 'lucide-react';
import { Button, Input } from 'ui-common-components';

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  /** Red confirm button + warning icon (destructive actions). Default true for confirm. */
  danger?: boolean;
  /** Prompt-only: initial input value + placeholder. */
  defaultValue?: string;
  placeholder?: string;
}

interface DialogState extends ConfirmOptions {
  kind: 'confirm' | 'alert' | 'prompt';
  resolve: (value: any) => void;
}

// Module-level pub/sub so the imperative API can reach the mounted host.
let emit: ((s: DialogState) => void) | null = null;

function open(state: Omit<DialogState, 'resolve'>): Promise<any> {
  return new Promise<any>((resolve) => {
    if (!emit) {
      // Host not mounted — fall back to native so the app never silently no-ops.
      if (state.kind === 'alert') { window.alert(state.message); resolve(true); }
      else if (state.kind === 'prompt') resolve(window.prompt(state.message, state.defaultValue ?? '') );
      else resolve(window.confirm(state.message));
      return;
    }
    emit({ ...state, resolve });
  });
}

/** Promise<boolean> — true when the user confirms. Drop-in for `confirm()`. */
export function confirmDialog(opts: string | ConfirmOptions): Promise<boolean> {
  const o = typeof opts === 'string' ? { message: opts } : opts;
  return open({ kind: 'confirm', danger: true, ...o });
}

/** Promise<void> — resolves when dismissed. Drop-in for `alert()`. */
export function alertDialog(opts: string | Omit<ConfirmOptions, 'cancelText' | 'danger'>): Promise<void> {
  const o = typeof opts === 'string' ? { message: opts } : opts;
  return open({ kind: 'alert', ...o }).then(() => undefined);
}

/** Promise<string|null> — entered text, or null if cancelled. Drop-in for `prompt()`. */
export function promptDialog(opts: string | Omit<ConfirmOptions, 'danger'>): Promise<string | null> {
  const o = typeof opts === 'string' ? { message: opts } : opts;
  return open({ kind: 'prompt', ...o });
}

export default function ConfirmHost() {
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [input, setInput] = useState('');

  useEffect(() => {
    emit = (s) => { setInput(s.defaultValue ?? ''); setDialog(s); };
    return () => { emit = null; };
  }, []);

  const finish = (confirmed: boolean) => {
    setDialog((d) => {
      if (d) {
        d.resolve(d.kind === 'prompt' ? (confirmed ? input.trim() : null) : confirmed);
      }
      return null;
    });
  };

  useEffect(() => {
    if (!dialog) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish(false);
      if (e.key === 'Enter') finish(true);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialog, input]);

  if (!dialog) return null;

  const isAlert = dialog.kind === 'alert';
  const isPrompt = dialog.kind === 'prompt';
  const danger = dialog.danger ?? false;
  const Icon = (isAlert || isPrompt) ? Info : AlertTriangle;
  const iconWrap = (isAlert || isPrompt) ? 'bg-blue-100' : danger ? 'bg-red-100' : 'bg-amber-100';
  const iconColor = (isAlert || isPrompt) ? 'text-blue-600' : danger ? 'text-red-600' : 'text-amber-600';

  return createPortal(
    // z-[10000]: above every app overlay (full-screen editors use z-[9999],
    // nested modals z-[60]/z-[100]) so confirm/alert dialogs are always on top.
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className={`flex-shrink-0 w-10 h-10 rounded-full ${iconWrap} flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${iconColor}`} />
          </div>
          <div className="min-w-0 flex-1">
            {dialog.title && <h3 className="text-lg font-semibold text-gray-900">{dialog.title}</h3>}
            <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap break-words">{dialog.message}</p>
            {isPrompt && (
              <Input
                size="sm"
                fullWidth
                className="mt-3"
                type="text"
                value={input}
                onChange={setInput}
                placeholder={dialog.placeholder}
                autoFocus
              />
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          {!isAlert && (
            <Button type="button" variant="outlineSecondary" size="sm" className="min-h-11" onClick={() => finish(false)}>
              {dialog.cancelText || 'Cancel'}
            </Button>
          )}
          <Button
            type="button"
            variant={danger && !isAlert && !isPrompt ? 'danger' : 'primary'}
            size="sm"
            className="min-h-11"
            onClick={() => finish(true)}
            disabled={isPrompt && !input.trim()}
            autoFocus={!isPrompt}
          >
            {dialog.confirmText || (isAlert ? 'OK' : isPrompt ? 'Create' : 'Confirm')}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
